// @school-apps/core/lib/markdown — a tiny, SAFE markdown micro-subset renderer (C·5).
//
// SECURITY MODEL: ESCAPE FIRST, then transform on the escaped text. Every `&<>"'` in the input
// becomes an entity BEFORE any markup is added, so the ONLY real tags in the output are the ones this
// module emits — a fixed allowlist: strong, em, code, ul, ol, li, br. There is NO links / images /
// headings / raw-HTML passthrough, ever. Output is safe to hand to dangerouslySetInnerHTML.
//
// Supported: **bold**, *em*, `code`, "- " unordered lists, "1. " ordered lists, newline -> <br>.
// Unclosed markers degrade to literal (escaped) text. Legacy plain text renders byte-visibly unchanged.

const ESCAPE = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Escape every HTML-significant character. Runs first, so no input can introduce a real tag. */
function escapeHtml(s) {
  return s.replace(/[&<>"']/g, (c) => ESCAPE[c]);
}

/**
 * Inline formatting over ALREADY-ESCAPED text. Order matters: `code` first (so ** inside code isn't
 * transformed), then **bold**, then *em*. Each marker needs a matching close or it stays literal.
 */
function inline(escaped) {
  return escaped
    .replace(/`([^`]+)`/g, (_m, g) => `<code>${g}</code>`)
    .replace(/\*\*([^*]+)\*\*/g, (_m, g) => `<strong>${g}</strong>`)
    .replace(/\*([^*]+)\*/g, (_m, g) => `<em>${g}</em>`);
}

const UL = /^- (.*)$/;
const OL = /^\d+\. (.*)$/;

/**
 * Render the markdown micro-subset to a safe HTML string.
 * @param {string} text
 * @returns {string} HTML using only the allowlisted tags
 */
export function renderMarkdownSubset(text) {
  const lines = escapeHtml(String(text ?? "")).split(/\r?\n/);
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const isUl = UL.test(lines[i]);
    const isOl = !isUl && OL.test(lines[i]);
    if (isUl || isOl) {
      const tag = isUl ? "ul" : "ol";
      const re = isUl ? UL : OL;
      const items = [];
      while (i < lines.length && re.test(lines[i])) {
        items.push(`<li>${inline(lines[i].match(re)[1])}</li>`);
        i++;
      }
      out.push(`<${tag}>${items.join("")}</${tag}>`);
    } else {
      // a run of non-list lines, joined by <br> (list markers end the run)
      const para = [];
      while (i < lines.length && !UL.test(lines[i]) && !OL.test(lines[i])) {
        para.push(inline(lines[i]));
        i++;
      }
      out.push(para.join("<br>"));
    }
  }
  return out.join("");
}
